<script setup lang="ts">
import * as yup from "yup"
import type { FormSubmitEvent } from "#ui/types"
import type { components } from "assets/api/openapi"
import {
  closeSlideover,
  errorHandler,
  useOperationAdditionalDataPugStore,
  formErrorHandler,
} from "#imports"

interface ForeignTrainingTemplateEditorProps {
  foreignTraining: components["schemas"]["TrainingTemplate"]
}

const props = defineProps<ForeignTrainingTemplateEditorProps>()
const emit = defineEmits<(event: "refreshData") => void>()

const operationAdditionalDataPugStore = useOperationAdditionalDataPugStore()
const operationStore = useOperationStore()
const { t } = useI18n()

const NAME_MAX_LENGTH = 100
const DESCRIPTION_MAX_LENGTH = 5000

const form = ref()
const { saveForeignTraining } = useForeignTrainingData()

const { startLoading, stopLoading } = useLoading()
const unsavedChanges = useState<boolean>("slideoverChanges", () => false)

const formFields = ref<components["schemas"]["ForeignTraining"]>({})

const formSchema = yup.object().shape({
  name: yup
    .string()
    .required("Pflichtfeld")
    .max(NAME_MAX_LENGTH, `Maximal ${NAME_MAX_LENGTH} Zeichen erlaubt`),
  description: yup
    .string()
    .max(
      DESCRIPTION_MAX_LENGTH,
      `Maximal ${DESCRIPTION_MAX_LENGTH} Zeichen erlaubt`
    ),
})

type YupSchema = yup.InferType<typeof formSchema>

const onSubmit = async (event: FormSubmitEvent<YupSchema>) => {
  try {
    startLoading()

    const payload: components["schemas"]["ForeignTraining"] = {
      ...formFields.value,
      ...event.data,
    }

    const { data, status, error } = await saveForeignTraining(payload)

    if ((status === 200 || status === 201) && data) {
      emit("refreshData")
      showToast("success", {
        description: "Vergleich wurde erfolgreich als Vorlage gespeichert",
      })
      unsavedChanges.value = false
      await closeSlideover()
    } else {
      form.value?.setErrors(errorHandler(status, error))
    }
  } catch (error) {
    console.error("Fehler beim Speichern:", error)
    showToast("danger", {
      description: "Vergleich konnte nicht als Vorlage gespeichert werden",
    })
  } finally {
    stopLoading()
  }
}

const trainingTemplateToForeignTraining = (
  trainingTemplate: components["schemas"]["TrainingTemplate"]
): components["schemas"]["ForeignTraining"] => ({
  name: "",
  description: trainingTemplate.comment ?? "",
  differences: trainingTemplate.trainingGroups
    ? Object.values(trainingTemplate.trainingGroups)
        .flat()
        .filter(
          (
            assessment
          ): assessment is components["schemas"]["TrainingAssessment"] =>
            assessment !== undefined
        )
        .map((assessment) => ({
          educationSubjectId: assessment.subjectId,
          educationSubjectGroupId: assessment.subjectGroupId,
          refreshTrainingHoursCount: assessment.refreshTrainingHoursCount,
          comment: undefined,
        }))
    : [],
  professionalGroupId:
    operationAdditionalDataPugStore.data?.educationProfession?.id,
  country: operationAdditionalDataPugStore.data?.educationState,
})

watch(
  formFields,
  () => {
    unsavedChanges.value = true
  },
  { deep: true }
)

onMounted(async () => {
  if (operationStore.operation?.id) {
    await operationAdditionalDataPugStore.fetchIfNeeded(
      operationStore.operation?.id
    )
  }
  formFields.value = trainingTemplateToForeignTraining(props.foreignTraining)
  await nextTick()
  unsavedChanges.value = false
})
</script>

<template>
  <IffSlideover
    headline-text="Vergleich als Vorlage speichern
    "
  >
    <UForm
      :validate-on="['input', 'submit']"
      ref="form"
      :schema="formSchema"
      :state="formFields"
      @submit="onSubmit"
      @error="formErrorHandler"
      class="mb-5"
      novalidate
    >
      <UFormGroup
        label="Bezeichnung"
        name="name"
        required
        :help="
          t('iff.common.characters-left', {
            AMOUNT: NAME_MAX_LENGTH - (formFields.name?.length ?? 0),
          })
        "
      >
        <UInput
          v-model.trim="formFields.name"
          aria-required="true"
          :maxlength="NAME_MAX_LENGTH"
        />
      </UFormGroup>

      <UFormGroup
        label="Begründung"
        name="description"
        :help="
          t('iff.common.characters-left', {
            AMOUNT:
              DESCRIPTION_MAX_LENGTH - (formFields.description?.length ?? 0),
          })
        "
      >
        <UTextarea
          v-model.trim="formFields.description"
          :maxlength="DESCRIPTION_MAX_LENGTH"
          :rows="6"
        />
      </UFormGroup>

      <div class="inline-grid grid-flow-col gap-3 auto-cols-max mt-6">
        <UButton variant="outline" @click="closeSlideover()" class="button">
          Abbrechen
        </UButton>
        <UButton type="submit">Absenden</UButton>
      </div>
    </UForm>
  </IffSlideover>
</template>
